/**
 * Built-in sampled instruments (F-LIB-05): a manifest (public/sounds/index.json,
 * generated by scripts/build-sounds.mjs) plus MP3 samples served with the app.
 * Instruments are fetched and decoded only when a track uses them, once per
 * AudioContext. The manifest is validated like any other input.
 */
import { z } from "zod";
import { leadingSilenceSec, type SampleSet } from "../audio/instruments/multisample";
import { AppError } from "../core/errors";
import { httpsUrl, soundIdSchema } from "../project/schema";

export const SOUNDS_FORMAT = "music-platform-sounds";

const midi = z.number().int().min(0).max(127);
const sourceSchema = z.object({
  name: z.string().max(200), author: z.string().max(300), url: httpsUrl,
  commit: z.string().regex(/^[0-9a-f]{40}$/), licence: z.string().max(40),
});
const soundSchema = z.object({
  id: soundIdSchema,
  name: z.string().min(1).max(80),
  category: z.string().min(1).max(40),
  type: z.enum(["pitched", "drums"]),
  source: z.string().max(40),
  releaseSec: z.number().min(0).max(10),
  tuneCents: z.number().min(-100).max(100).optional(),
  pitchCheck: z.enum(["octave-agnostic"]).optional(),
  // Relative path inside sounds/: no scheme, no "..", no leading slash.
  zones: z.array(z.object({ root: midi, lo: midi, hi: midi, file: z.string().regex(/^[a-z0-9-]+\/[A-Za-z0-9-]+\.mp3$/) })).min(1).max(128),
});
export const soundsManifestSchema = z.object({
  format: z.literal(SOUNDS_FORMAT),
  version: z.literal(1),
  sources: z.record(z.string(), sourceSchema),
  instruments: z.array(soundSchema).max(200),
}).refine((m) => m.instruments.every((i) => i.source in m.sources), { message: "Instrument refers to an unknown source" });

export type SoundsManifest = z.infer<typeof soundsManifestSchema>;
export type SoundInfo = z.infer<typeof soundSchema>;

export interface SoundLibraryDeps {
  /** Fetch bytes of a path relative to the sounds folder. */
  fetchBytes?: (path: string) => Promise<ArrayBuffer>;
  /** Base URL of the app (Vite BASE_URL). */
  base?: string;
}

export class SoundLibrary {
  private manifestPromise: Promise<SoundsManifest> | null = null;
  private sets = new WeakMap<BaseAudioContext, Map<string, Promise<SampleSet>>>();

  constructor(private deps: SoundLibraryDeps = {}) {}

  private fetchBytes(path: string): Promise<ArrayBuffer> {
    if (this.deps.fetchBytes) return this.deps.fetchBytes(path);
    const url = `${this.deps.base ?? "/"}sounds/${path}`;
    return fetch(url).then((r) => {
      if (!r.ok) throw new AppError("SOUND_LOAD_FAILED", `Could not download ${path} (HTTP ${r.status}).`);
      return r.arrayBuffer();
    });
  }

  /** The list of built-in instruments (cached; a failed load is retried next time). */
  manifest(): Promise<SoundsManifest> {
    this.manifestPromise ??= this.fetchBytes("index.json").then((bytes) => {
      const parsed = soundsManifestSchema.safeParse(JSON.parse(new TextDecoder().decode(bytes)));
      if (!parsed.success) throw new AppError("SOUND_LOAD_FAILED", "The built-in sound list is invalid.");
      return parsed.data;
    }).catch((err: unknown) => {
      this.manifestPromise = null;
      throw err;
    });
    return this.manifestPromise;
  }

  async info(id: string): Promise<SoundInfo | undefined> {
    return (await this.manifest()).instruments.find((i) => i.id === id);
  }

  /** Download and decode an instrument for a context (shared by every track using it). */
  load(ctx: BaseAudioContext, id: string): Promise<SampleSet> {
    let perCtx = this.sets.get(ctx);
    if (!perCtx) this.sets.set(ctx, (perCtx = new Map()));
    const cached = perCtx.get(id);
    if (cached) return cached;
    const p = this.decode(ctx, id);
    perCtx.set(id, p);
    p.catch(() => perCtx.delete(id)); // allow a retry after a network error
    return p;
  }

  private async decode(ctx: BaseAudioContext, id: string): Promise<SampleSet> {
    const info = await this.info(id);
    if (!info) throw new AppError("SOUND_UNKNOWN", `This version does not include the instrument "${id}".`);
    const zones = await Promise.all(info.zones.map(async (z) => {
      const bytes = await this.fetchBytes(z.file);
      let buffer: AudioBuffer;
      try { buffer = await ctx.decodeAudioData(bytes); } catch (e) {
        throw new AppError("AUDIO_DECODE_FAILED", `This browser could not decode ${z.file}.`, { cause: e });
      }
      return { root: z.root, lo: z.lo, hi: z.hi, buffer, offsetSec: leadingSilenceSec(buffer) };
    }));
    return { zones, tuneCents: info.tuneCents, oneShot: info.type === "drums" };
  }
}
